import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import {
  useAbundanceViewsInNavigation,
  useAbundanceEngineViews
} from '../../redux/slices/abundanceEngineContext'
import { useCheckPolicies } from '../../api/policy'
import { useFeatureFlags } from '../../redux/slices/appConfig'
import { ADMIN } from '../../policies/admin'
import { ADMIN_ROUTES, DATA_HOME_ROUTES, FEATURE_FLAG } from '../../constants'
import { postNamedQuery } from '../../service'

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1500,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: '20vh'
  },
  container: {
    width: '100%',
    maxWidth: 560,
    backgroundColor: 'var(--background-default, #fff)',
    borderRadius: 8,
    boxShadow: '0 16px 70px rgba(0,0,0,0.3)',
    overflow: 'hidden'
  },
  input: {
    width: '100%',
    padding: '14px 20px',
    fontSize: 18,
    border: 'none',
    outline: 'none',
    backgroundColor: 'var(--background-default, #fff)',
    color: 'var(--text-color-main, #333)',
    boxSizing: 'border-box',
    borderBottom: '1px solid var(--color-neutral-500, #e0e0e0)',
    fontFamily: 'inherit'
  },
  resultsList: {
    maxHeight: 48 * 7,
    overflowY: 'auto',
    margin: 0,
    padding: 0,
    listStyle: 'none'
  },
  resultItem: {
    padding: '12px 20px',
    cursor: 'pointer',
    fontSize: 15,
    color: 'var(--text-color-main, #333)',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-200, #f5f5f5)'
    }
  },
  resultItemActive: {
    backgroundColor: 'var(--color-neutral-300, #e8e8e8)'
  },
  subItem: {
    paddingLeft: 36
  },
  parentLabel: {
    fontSize: 12,
    color: 'var(--text-color-secondary, #999)',
    marginLeft: 8
  },
  noResults: {
    padding: '12px 20px',
    fontSize: 14,
    color: 'var(--text-color-secondary, #999)'
  },
  loading: {
    padding: '12px 20px',
    fontSize: 14,
    color: 'var(--text-color-secondary, #999)'
  }
}))

function matchAllTerms (text, terms) {
  const lower = text.toLowerCase()
  return terms.every((t) => lower.includes(t))
}

const VIEWPATH_RE = /(?:viewPath|to)=["'{]*(\/views\/)?([^"'\s}]+)/g
const TITLE_RE = /title=["']([^"']+)["'][\s\S]*?viewPath=["']([^"']+)["']/g

function extractLinksFromJsx (jsx) {
  const links = new Map()

  let match
  while ((match = TITLE_RE.exec(jsx)) !== null) {
    links.set(match[2], match[1])
  }
  TITLE_RE.lastIndex = 0

  while ((match = VIEWPATH_RE.exec(jsx)) !== null) {
    const path = match[2].replace(/^\/views\//, '')
    if (!links.has(path)) {
      links.set(path, null)
    }
  }
  VIEWPATH_RE.lastIndex = 0

  return links
}

const ADMIN_ITEMS = [
  { title: 'Households', path: `${ADMIN_ROUTES.CLIENTS}?tab=households`, policy: 'admin_view_clients' },
  { title: 'Accounts', path: ADMIN_ROUTES.ACCOUNTS, policyAny: ['admin_view_accts', 'admin_view_managers'] },
  { title: 'Registrations', path: ADMIN_ROUTES.REGISTRATIONS, policy: 'admin_view_accts' },
  { title: 'Advisors', path: ADMIN_ROUTES.ADVISORS, policy: 'admin_view_advisor_users', featureFlag: FEATURE_FLAG.ADVISOR_AND_ROLE_SETUP },
  { title: 'User Management', path: ADMIN_ROUTES.USER_BATCHES, policy: 'admin_view_users_batches' },
  { title: 'Roles', path: ADMIN_ROUTES.ROLES, policy: 'admin_view_advisor_roles', featureFlag: FEATURE_FLAG.ADVISOR_AND_ROLE_SETUP },
  { title: 'Products', path: ADMIN_ROUTES.CLASSIFY_ASSETS, policy: 'admin_view_assets', featureFlag: FEATURE_FLAG.ASSET_CLASSIFICATION_SETUP },
  { title: 'Billing', path: ADMIN_ROUTES.BILLING, policyAny: ['admin_view_feeschedules', 'admin_run_billing'], featureFlag: FEATURE_FLAG.SHOW_BILLING_INFO },
  { title: 'Integrations', path: ADMIN_ROUTES.INTEGRATIONS, policy: 'admin_view_integrations', featureFlag: FEATURE_FLAG.INTEGRATIONS },
  { title: 'Reports', path: ADMIN_ROUTES.REPORTS, policy: 'admin_run_reports', featureFlag: FEATURE_FLAG.REPORTS },
  { title: 'Infographics', path: ADMIN_ROUTES.INFOGRAPHIC_REPORTS, policy: 'admin_view_info_reports', featureFlag: FEATURE_FLAG.INFOGRAPHIC },
  { title: 'Vault', path: ADMIN_ROUTES.VAULT, policy: 'admin_view_accts' },
  { title: 'Data Home', path: DATA_HOME_ROUTES.DATA_HOME, policy: 'admin_view_datahome' },
  { title: 'Status Reports', path: ADMIN_ROUTES.STATUS_REPORTS.HOME, policy: 'admin_view_status_templates' },
  { title: 'Models', path: ADMIN_ROUTES.TARGET_MODELS, policy: 'admin_models_view' },
  { title: 'Configurations', path: ADMIN_ROUTES.DATA_MAINTENANCE, policyAny: ['admin_view_asset_tagtypes', 'admin_view_benchmarks', 'admin_view_firm_goals'] },
  { title: 'Media Management', path: ADMIN_ROUTES.MEDIA, policy: 'admin_edit_media' },
  { title: 'Denali', path: ADMIN_ROUTES.DENALI.HOME, policy: 'denali_admin' }
]

let subViewCache = null

function CommandPalette () {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [subViews, setSubViews] = useState(subViewCache?.items || [])
  const [subViewsLoading, setSubViewsLoading] = useState(false)
  const inputRef = useRef(null)
  const listRef = useRef(null)
  const classes = useStyles()
  const history = useHistory()
  const viewsInNavigation = useAbundanceViewsInNavigation()
  const allViews = useAbundanceEngineViews()
  const { data: policies } = useCheckPolicies()
  const featureFlags = useFeatureFlags()

  const canViewAdmin = Boolean(policies?.[ADMIN.viewAdmin])

  const adminItems = useMemo(() => {
    if (!canViewAdmin || !policies) return []

    return ADMIN_ITEMS.filter((item) => {
      if (item.featureFlag) {
        const flag = featureFlags?.[item.featureFlag]
        if (!flag?.active) return false
      }
      if (item.policy) return Boolean(policies[item.policy])
      if (item.policyAny) return item.policyAny.some((p) => Boolean(policies[p]))
      return true
    }).map((item) => ({
      name: item.title,
      path: item.path,
      parentName: 'Admin',
      isSubView: true,
      isAdmin: true
    }))
  }, [canViewAdmin, policies, featureFlags])

  const viewsByPath = useMemo(() => {
    const map = {}
    allViews.forEach((v) => { map[v.path] = v })
    return map
  }, [allViews])

  const fetchSubViews = useCallback(async () => {
    if (subViewCache) {
      setSubViews(subViewCache.items)
      return
    }
    setSubViewsLoading(true)
    try {
      const results = await Promise.allSettled(
        viewsInNavigation.map(async (parentView) => {
          const resp = await postNamedQuery('abundanceEngine', 'get-view-by-path-v2', { path: parentView.path })
          const jsx = resp?.data?.view?.content || ''
          const linkMap = extractLinksFromJsx(jsx)
          return { parentView, linkMap }
        })
      )

      const items = []
      const seen = new Set(viewsInNavigation.map((v) => v.path))

      results.forEach((result) => {
        if (result.status !== 'fulfilled') return
        const { parentView, linkMap } = result.value
        linkMap.forEach((title, viewPath) => {
          if (seen.has(viewPath)) return
          seen.add(viewPath)

          const resolved = viewsByPath[viewPath]
          const name = resolved?.name || title || viewPath
          items.push({
            name,
            path: viewPath,
            parentName: parentView.name,
            isSubView: true
          })
        })
      })

      subViewCache = { items }
      setSubViews(items)
    } catch (err) {
      console.error('Failed to fetch sub-views for command palette', err)
    } finally {
      setSubViewsLoading(false)
    }
  }, [viewsInNavigation, viewsByPath])

  const allItems = useMemo(() => {
    const subViewsByParent = {}
    subViews.forEach((sv) => {
      if (!subViewsByParent[sv.parentName]) subViewsByParent[sv.parentName] = []
      subViewsByParent[sv.parentName].push(sv)
    })

    const items = []
    viewsInNavigation.forEach((v) => {
      items.push({ ...v, isSubView: false, parentName: null })
      const children = subViewsByParent[v.name]
      if (children) items.push(...children)
    })

    if (canViewAdmin) {
      items.push({ name: 'Admin', path: '/admin', isSubView: false, parentName: null, isAdmin: true })
      items.push(...adminItems)
    }

    return items
  }, [viewsInNavigation, subViews, canViewAdmin, adminItems])

  const results = useMemo(() => {
    if (!query.trim()) return allItems
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean)
    return allItems.filter(({ name, parentName }) => {
      const combined = parentName ? `${name} ${parentName}` : name
      return matchAllTerms(combined, terms)
    })
  }, [query, allItems])

  const close = useCallback(() => {
    setOpen(false)
    setQuery('')
    setActiveIndex(0)
  }, [])

  const navigateTo = useCallback((item) => {
    if (item.isAdmin) {
      history.push(item.path)
    } else {
      history.push(`/${item.primary ? '' : 'views/'}${item.path}`)
    }
    close()
  }, [history, close])

  useEffect(() => {
    if (!canViewAdmin) return
    function handleKeyDown (e) {
      if (e.metaKey && e.shiftKey && e.code === 'KeyP') {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [canViewAdmin])

  useEffect(() => {
    if (open) {
      if (inputRef.current) inputRef.current.focus()
      if (viewsInNavigation.length > 0 && subViews.length === 0 && !subViewCache) {
        fetchSubViews()
      }
    }
  }, [open, viewsInNavigation.length, subViews.length, fetchSubViews])

  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  useEffect(() => {
    if (!listRef.current) return
    const active = listRef.current.children[activeIndex]
    if (active) {
      active.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIndex])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      close()
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev))
      return
    }
    if (e.key === 'Enter' && results.length > 0) {
      e.preventDefault()
      navigateTo(results[activeIndex])
    }
  }, [results, activeIndex, navigateTo, close])

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      close()
    }
  }, [close])

  if (!canViewAdmin || !open) return null

  return (
    <div className={classes.overlay} onClick={handleOverlayClick}>
      <div className={classes.container}>
        <input
          ref={inputRef}
          className={classes.input}
          type='text'
          placeholder='Search views...'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        {results.length > 0 ? (
          <ul className={classes.resultsList} ref={listRef}>
            {results.map((item, index) => (
              <li
                key={`${item.path}-${item.isSubView}`}
                className={`${classes.resultItem} ${item.isSubView ? classes.subItem : ''} ${index === activeIndex ? classes.resultItemActive : ''}`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => navigateTo(item)}
              >
                {item.name}
                {item.isSubView && item.parentName && (
                  <span className={classes.parentLabel}>{item.parentName}</span>
                )}
              </li>
            ))}
          </ul>
        ) : query.trim() ? (
          <div className={classes.noResults}>No matching views</div>
        ) : null}
        {subViewsLoading && (
          <div className={classes.loading}>Loading sub-views...</div>
        )}
      </div>
    </div>
  )
}

export default CommandPalette
